function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function pickTrimmedString(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return null;
}

function nestedBookerName(record: Record<string, unknown> | null): string | null {
  if (!record) {
    return null;
  }
  for (const key of ['name', 'full_name', 'display_name', 'client_name', 'user_name'] as const) {
    const value = pickTrimmedString(record[key]);
    if (value) {
      return value;
    }
  }
  const first = pickTrimmedString(record.first_name);
  const last = pickTrimmedString(record.last_name);
  if (first && last) {
    return `${first} ${last}`;
  }
  return first ?? last;
}

/** Human label for who placed the booking (client / worker). */
export function bookingRecordBookerLabel(booking: Record<string, unknown>): string {
  for (const key of [
    'client_name',
    'booker_name',
    'customer_name',
    'user_name',
    'guest_name',
  ] as const) {
    const direct = pickTrimmedString(booking[key]);
    if (direct) {
      return direct;
    }
  }

  for (const nestKey of ['client', 'user', 'booker', 'customer', 'guest'] as const) {
    const nested = booking[nestKey];
    if (isPlainObject(nested)) {
      const name = nestedBookerName(nested);
      if (name) {
        return name;
      }
    }
  }

  const clientId = pickTrimmedString(booking.client_id) ?? pickTrimmedString(booking.user_id);
  if (clientId) {
    return clientId;
  }

  return '';
}

/** Compact initials for narrow calendar cells (e.g. weekly owner view). */
export function bookingRecordBookerInitials(booking: Record<string, unknown>): string {
  const label = bookingRecordBookerLabel(booking);
  return bookerLabelToInitials(label);
}

export function bookerLabelToInitials(label: string): string {
  const trimmed = label.trim();
  if (!trimmed) {
    return '?';
  }

  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const first = parts[0]!;
    const last = parts[parts.length - 1]!;
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  }

  const word = parts[0]!;
  if (word.length >= 2) {
    return word.slice(0, 2).toUpperCase();
  }
  return word.charAt(0).toUpperCase();
}

export function bookingRecordStatusDisplay(booking: Record<string, unknown>): string {
  const statusName = pickTrimmedString(booking.status_name) ?? pickTrimmedString(booking.status);
  if (statusName) {
    return statusName;
  }
  const statusId = booking.status_id;
  if (typeof statusId === 'number' && Number.isFinite(statusId)) {
    return String(statusId);
  }
  if (typeof statusId === 'string' && statusId.trim()) {
    return statusId.trim();
  }
  return '';
}
